import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye, FileSpreadsheet, FileText, ListChecks, Loader2, Pencil, Printer, RefreshCw, Trash2, Trophy, Users } from 'lucide-react';
import {
  AdminProgramCandidate,
  CandidateItemDetails,
  CertificateStatus,
  ExportType,
  ItemResultRow,
  MarkEntryRow,
  ProgramCandidatesAdminApi,
  ProgramDashboardApi,
  ProgramDashboardAdminApi,
  ProgramItem,
  ProgramItemsApi,
  SchoolRegistrationStats,
} from '@/services/program-dashboard.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { Modal } from '@/components/ui/Modal';
import type { ExportFormat } from '@/services/school.api';
import { useAuthStore } from '@/stores/auth.store';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}

const GRADE_TONE: Record<string, 'green' | 'blue' | 'amber' | 'slate'> = { A: 'green', B: 'blue', C: 'amber' };

/**
 * Shared across every tab that reads marks/results/registrations.
 * Defaults the filter to whichever year is actually `isCurrent` (fetched
 * live, not hardcoded) — confirmed the real current year ("2026-27") has
 * zero registrations yet (every real registration is still "2025-26",
 * no longer current anywhere), so defaulting here correctly shows an
 * empty dashboard rather than silently surfacing last year's competition
 * as if it were live. `setAcademicYear` marks the choice as deliberate
 * ("touched") so this default-to-current effect never fights a caller who
 * explicitly picks "All Years" (empty string) or an older year.
 */
function useProgramYearFilter() {
  const { data: academicYears = [] } = useQuery({ queryKey: ['program-academic-years'], queryFn: () => ProgramDashboardAdminApi.academicYears() });
  const [academicYear, setAcademicYearRaw] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (touched) return;
    const current = academicYears.find((y) => y.isCurrent);
    if (current) setAcademicYearRaw(current.name);
  }, [academicYears, touched]);

  const setAcademicYear = (v: string) => {
    setTouched(true);
    setAcademicYearRaw(v);
  };

  return { academicYears, academicYear, setAcademicYear };
}

function YearFilterField({ value, onChange, years }: { value: string; onChange: (v: string) => void; years: { name: string; isCurrent: boolean }[] }) {
  return (
    <Field label="Session Year">
      <Select value={value} onChange={(e) => onChange(e.target.value)} className="!w-40">
        <option value="">All Years</option>
        {years.map((y) => (
          <option key={y.name} value={y.name}>
            {y.name}
            {y.isCurrent ? ' (current)' : ''}
          </option>
        ))}
      </Select>
    </Field>
  );
}

const ALL_TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'mark-entry', label: 'Mark Entry' },
  { key: 'item-results', label: 'Item Results' },
  { key: 'top-score', label: 'Item-wise Top Score' },
  { key: 'scoreboard', label: 'Scoreboard' },
  { key: 'candidates', label: 'Candidates' },
  { key: 'stats', label: 'Registration Stats' },
  { key: 'all-students', label: 'All Students' },
  { key: 'exports', label: 'Exports' },
] as const;

type TabKey = (typeof ALL_TABS)[number]['key'];

/**
 * Candidate registration edits (Candidates tab) stay platform-tier only —
 * not part of this reopening, per direct instruction. Mark Entry (judging)
 * WAS platform-tier only too (see DEVIATIONS.md's Program Management
 * section — a prior task explicitly reversed org-tier write access down
 * to read-only), but was reopened again per direct instruction, scoped to
 * an org admin's OWN schools only this time (never cross-org) — enforced
 * server-side by `ProgramSchoolsScopeService.resolveSchool()`, not just
 * hidden here.
 */
const ORG_TIER_HIDDEN_TABS = new Set<TabKey>(['candidates']);

/**
 * Program Management, Part 2 of 3 — Program Dashboard. Platform tier
 * (superadmin) sees every tab, including judging/ranking/candidate edits —
 * ranking is inherently cross-school (see ProgramDashboardService's doc
 * comment), so this UI shows every registered school's candidates together
 * per item, not one school at a time. Registration itself is the
 * school-tier Candidate Registration page from Part 1.
 *
 * Organization tier (`user.scope === 'organization'`) reuses the exact same
 * routes/queries (already org-scoped server-side, see
 * ProgramSchoolsScopeService) but read-only: Mark Entry and Candidates are
 * hidden, since judging/registration edits stay platform-only.
 */
export function ProgramDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isOrgTier = user?.scope === 'organization';
  const tabs = isOrgTier ? ALL_TABS.filter((t) => !ORG_TIER_HIDDEN_TABS.has(t.key)) : ALL_TABS;
  const [tab, setTab] = useState<TabKey>('dashboard');
  // "View All" on a grade card deep-links into All Students pre-filtered —
  // lifted here since Dashboard and All Students are separate tabs/components.
  const [pendingGrade, setPendingGrade] = useState('');

  return (
    <>
      <PageHeader
        title="Program Dashboard"
        description={
          isOrgTier
            ? "Read-only view of your organization's schools' registrations, results, and standing within the competition."
            : 'Judging, ranking, scoreboards, and certificates for the competition catalog.'
        }
      />

      <div className="mb-4 flex items-center gap-1 overflow-x-auto border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && (
        <DashboardTab
          onNavigate={setTab}
          onViewAllGrade={(grade) => {
            setPendingGrade(grade);
            setTab('all-students');
          }}
        />
      )}
      {tab === 'mark-entry' && <MarkEntryTab />}
      {tab === 'item-results' && <ItemResultsTab />}
      {tab === 'top-score' && <ItemWiseTopScoreTab />}
      {tab === 'scoreboard' && <ScoreboardTab />}
      {tab === 'candidates' && !isOrgTier && <CandidatesTab />}
      {tab === 'stats' && <RegistrationStatsTab />}
      {tab === 'all-students' && <AllStudentsTab initialGrade={pendingGrade} />}
      {tab === 'exports' && <ExportsTab />}
    </>
  );
}

// ───── Dashboard (default landing view) ───────────────────────────────────────
function DashboardTab({
  onNavigate,
  onViewAllGrade,
}: {
  onNavigate: (tab: TabKey) => void;
  onViewAllGrade: (grade: string) => void;
}) {
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();
  const { data, isFetching } = useQuery({
    queryKey: ['program-registration-stats', academicYear],
    queryFn: () => ProgramDashboardAdminApi.registrationStats(academicYear || undefined),
  });
  const [printBusy, setPrintBusy] = useState<string | null>(null);
  const [excelBusy, setExcelBusy] = useState<string | null>(null);

  const printResult = async (grade: string) => {
    if (printBusy) return;
    setPrintBusy(grade);
    try {
      await ProgramCandidatesAdminApi.printResult(grade, academicYear || undefined);
    } finally {
      setPrintBusy(null);
    }
  };
  const exportExcel = async (grade: string) => {
    if (excelBusy) return;
    setExcelBusy(grade);
    try {
      await ProgramCandidatesAdminApi.exportResult(grade, academicYear || undefined);
    } finally {
      setExcelBusy(null);
    }
  };

  if (isFetching || !data) return <div className="card p-8 text-center text-slate-400">Loading…</div>;

  const grades = data.byGrade.filter((g) => g.total > 0);
  const institutions = data.bySchool.filter((s) => s.total > 0).sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
      </div>

      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => onNavigate('all-students')}>
          <Users className="h-4 w-4" /> All Students List
        </button>
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => onNavigate('scoreboard')}>
          <ListChecks className="h-4 w-4" /> Score Board
        </button>
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => onNavigate('top-score')}>
          <Trophy className="h-4 w-4" /> Top Scorer
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {grades.length === 0 ? (
            <div className="card p-8 text-center text-slate-400">No registrations yet.</div>
          ) : (
            grades.map((g) => (
              <div key={g.grade} className="card p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-base font-semibold text-slate-800">{g.grade}</div>
                  <div className="flex flex-wrap gap-1.5">
                    <button className="btn-secondary !py-1 text-xs" onClick={() => onViewAllGrade(g.grade)}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> View All
                    </button>
                    <button className="btn-secondary !py-1 text-xs" onClick={() => onNavigate('mark-entry')}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Update Marks
                    </button>
                    <button className="btn-secondary !py-1 text-xs" onClick={() => onNavigate('top-score')}>
                      <ListChecks className="mr-1 h-3.5 w-3.5" /> Item Wise
                    </button>
                    <button className="btn-secondary !py-1 text-xs" disabled={printBusy === g.grade} onClick={() => printResult(g.grade)}>
                      {printBusy === g.grade ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-1 h-3.5 w-3.5" />} Print Result
                    </button>
                    <button className="btn-secondary !py-1 text-xs" disabled={excelBusy === g.grade} onClick={() => exportExcel(g.grade)}>
                      {excelBusy === g.grade ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="mr-1 h-3.5 w-3.5 text-green-600" />} Export
                      Excel
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Users className="h-3.5 w-3.5" /> Total Students
                    </div>
                    <div className="text-xl font-bold text-slate-900">{g.total}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Users className="h-3.5 w-3.5 text-blue-500" /> Male
                    </div>
                    <div className="text-xl font-bold text-slate-900">{g.male}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Users className="h-3.5 w-3.5 text-pink-500" /> Female
                    </div>
                    <div className="text-xl font-bold text-slate-900">{g.female}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="card overflow-hidden p-0">
          <div className="bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white">Institutions with Registrations</div>
          <div className="max-h-[32rem] overflow-y-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-slate-500">Institution</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold uppercase text-slate-500">Total</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold uppercase text-slate-500">M</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold uppercase text-slate-500">F</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {institutions.map((s) => (
                  <InstitutionRow key={s.schoolId} school={s} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function InstitutionRow({ school }: { school: SchoolRegistrationStats }) {
  const [busy, setBusy] = useState<'print' | 'excel' | null>(null);

  const print = async () => {
    if (busy) return;
    setBusy('print');
    try {
      await ProgramCandidatesAdminApi.exportParticipantsPdf(school.schoolId, school.schoolName);
    } finally {
      setBusy(null);
    }
  };
  const excel = async () => {
    if (busy) return;
    setBusy('excel');
    try {
      await ProgramCandidatesAdminApi.exportParticipantsExcel(school.schoolId, school.schoolName);
    } finally {
      setBusy(null);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-3 py-2 font-medium text-slate-800">{school.schoolName}</td>
      <td className="px-2 py-2 text-center text-slate-700">{school.total}</td>
      <td className="px-2 py-2 text-center text-slate-500">{school.male}</td>
      <td className="px-2 py-2 text-center text-slate-500">{school.female}</td>
      <td className="px-3 py-2 text-right">
        <button className="mr-1.5 text-slate-400 hover:text-brand-600" title="Print" disabled={!!busy} onClick={print}>
          {busy === 'print' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
        </button>
        <button className="text-slate-400 hover:text-green-600" title="Excel" disabled={!!busy} onClick={excel}>
          {busy === 'excel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
        </button>
      </td>
    </tr>
  );
}

function useItemPicker() {
  const { data: items = [] } = useQuery({ queryKey: ['program-items-catalog-sa'], queryFn: () => ProgramItemsApi.list() });
  const grades = useMemo(() => [...new Set(items.map((i) => i.gradeCategory).filter(Boolean))] as string[], [items]);
  const [grade, setGrade] = useState('');
  const [itemId, setItemId] = useState('');
  const itemsForGrade = items.filter((i) => !grade || i.gradeCategory === grade);
  useEffect(() => {
    setItemId('');
  }, [grade]);
  return { items, grades, grade, setGrade, itemId, setItemId, itemsForGrade };
}

// ───── Mark Entry ────────────────────────────────────────────────────────────
function MarkEntryTab() {
  const qc = useQueryClient();
  const { grades, grade, setGrade, itemId, setItemId, itemsForGrade } = useItemPicker();
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['program-mark-entry', itemId, academicYear],
    queryFn: () => ProgramDashboardApi.markEntry(itemId, academicYear || undefined),
    enabled: !!itemId,
  });

  const [draft, setDraft] = useState<Record<string, { judgeMark?: string; grade?: string }>>({});
  useEffect(() => setDraft({}), [itemId]);

  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (row: MarkEntryRow) => {
      const d = draft[row.candidateItemId];
      const judgeMark = d?.judgeMark !== undefined && d.judgeMark !== '' ? Number(d.judgeMark) : undefined;
      const gr = d?.grade && d.grade !== '' ? (d.grade as 'A' | 'B' | 'C') : undefined;
      return ProgramDashboardApi.setMark({ schoolId: row.schoolId, candidateItemId: row.candidateItemId, judgeMark, grade: gr });
    },
    onMutate: (row) => {
      setSavingId(row.candidateItemId);
      setError(undefined);
    },
    onSettled: () => setSavingId(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['program-mark-entry', itemId] }),
    onError: (e) => setError(errMsg(e)),
  });

  const recalc = useMutation({
    mutationFn: () => ProgramDashboardApi.recalculateRanks(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['program-mark-entry', itemId] }),
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
        <Field label="Grade">
          <Select value={grade} onChange={(e) => setGrade(e.target.value)} className="!w-40">
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Program Item">
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)} className="!w-64">
            <option value="">Select item…</option>
            {itemsForGrade.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.code ? `(${i.code})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        {itemId && (
          <button className="btn-secondary" onClick={() => recalc.mutate()} disabled={recalc.isPending}>
            {recalc.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
            Recalculate Ranks
          </button>
        )}
        {recalc.isSuccess && <span className="text-sm text-green-700">✓ Ranks recalculated ({recalc.data?.itemsRanked} entries).</span>}
      </div>

      {error && <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {!itemId ? (
        <div className="card p-8 text-center text-slate-400">Select a program item to enter marks.</div>
      ) : isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No candidates registered for this item yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Chest #</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Role</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Mark</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Save</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.candidateItemId} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-sm font-medium text-slate-800">{r.schoolName}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">{r.studentName}</td>
                  <td className="px-4 py-2.5 text-center text-sm text-slate-500">{r.chestNumber ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    {r.groupLeaderId === null && r.isGroupLeader ? <Badge tone="blue">Leader</Badge> : r.groupLeaderId ? <Badge tone="slate">Member</Badge> : ''}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="number"
                      min={0}
                      className="w-20 rounded-md border border-slate-200 px-2 py-1 text-center text-sm focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                      defaultValue={r.judgeMark ?? ''}
                      onChange={(e) => setDraft((p) => ({ ...p, [r.candidateItemId]: { ...p[r.candidateItemId], judgeMark: e.target.value } }))}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <Select
                      className="!w-20 !py-1 text-sm"
                      defaultValue={r.grade ?? ''}
                      onChange={(e) => setDraft((p) => ({ ...p, [r.candidateItemId]: { ...p[r.candidateItemId], grade: e.target.value } }))}
                    >
                      <option value="">—</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                    </Select>
                  </td>
                  <td className="px-4 py-2.5 text-center text-sm text-slate-600">{r.rank ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      className="btn-secondary !py-1 text-xs"
                      disabled={savingId === r.candidateItemId}
                      onClick={() => save.mutate(r)}
                    >
                      {savingId === r.candidateItemId ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───── Item Results ──────────────────────────────────────────────────────────
function ItemResultsTab() {
  const { grades, grade, setGrade, itemId, setItemId, itemsForGrade } = useItemPicker();
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();
  const [exporting, setExporting] = useState(false);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['program-item-results', itemId, academicYear],
    queryFn: () => ProgramDashboardApi.itemResults(itemId, academicYear || undefined),
    enabled: !!itemId,
  });

  const exportExcel = async () => {
    if (!itemId || exporting) return;
    setExporting(true);
    try {
      await ProgramDashboardApi.exportItemResults(itemId, academicYear || undefined);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
        <Field label="Grade">
          <Select value={grade} onChange={(e) => setGrade(e.target.value)} className="!w-40">
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Program Item">
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)} className="!w-64">
            <option value="">Select item…</option>
            {itemsForGrade.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.code ? `(${i.code})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <button
          className="btn-secondary flex items-center gap-1.5"
          onClick={exportExcel}
          disabled={!itemId || results.length === 0 || exporting}
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
          Export Excel
        </button>
      </div>

      {!itemId ? (
        <div className="card p-8 text-center text-slate-400">Select a program item to see results.</div>
      ) : isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : results.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No candidates for this item yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Chest #</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Mark</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Certificate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((r) => (
                <ItemResultRowView key={r.candidateItemId} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ItemResultRowView({ row }: { row: ItemResultRow }) {
  const [status, setStatus] = useState<CertificateStatus | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestCertificate() {
    setBusy(true);
    try {
      let s = await ProgramDashboardApi.certificate(row.schoolId, row.candidateItemId);
      // Poll until ready, matching Report Cards' own poll-until-done pattern.
      while (s.status === 'generating') {
        await new Promise((r) => setTimeout(r, 2000));
        s = await ProgramDashboardApi.certificate(row.schoolId, row.candidateItemId);
      }
      setStatus(s);
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-2.5 text-center text-sm font-semibold text-slate-800">{row.rank ?? '—'}</td>
      <td className="px-4 py-2.5 text-sm text-slate-700">
        {row.studentName}
        {row.isGroup && row.isGroupLeader && <Badge tone="blue" className="ml-1.5">Leader</Badge>}
      </td>
      <td className="px-4 py-2.5 text-sm text-slate-600">{row.schoolName}</td>
      <td className="px-4 py-2.5 text-center text-sm text-slate-500">{row.chestNumber ?? '—'}</td>
      <td className="px-4 py-2.5 text-center text-sm text-slate-600">{row.judgeMark ?? '—'}</td>
      <td className="px-4 py-2.5 text-center">
        {row.grade ? <Badge tone={GRADE_TONE[row.grade] ?? 'slate'}>{row.grade}</Badge> : '—'}
      </td>
      <td className="px-4 py-2.5 text-right">
        {status?.status === 'ready' && status.pdfUrl ? (
          <a href={status.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline">
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        ) : (
          <button className="btn-secondary !py-1 text-xs" disabled={busy} onClick={requestCertificate}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Generate'}
          </button>
        )}
      </td>
    </tr>
  );
}

// ───── Item-wise Top Score (A-grade only) ─────────────────────────────────────
function ItemWiseTopScoreTab() {
  const { grades, grade, setGrade, itemId, setItemId, itemsForGrade } = useItemPicker();
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();
  const [callSheetBusy, setCallSheetBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['program-item-wise-top-score', itemId, academicYear],
    queryFn: () => ProgramDashboardApi.itemWiseTopScore(itemId, academicYear || undefined),
    enabled: !!itemId,
  });

  const callSheet = async () => {
    if (!grade || callSheetBusy) return;
    setCallSheetBusy(true);
    try {
      await ProgramDashboardApi.exportData('call-sheet', 'pdf', { grade, academicYear: academicYear || undefined });
    } finally {
      setCallSheetBusy(false);
    }
  };
  const exportExcel = async () => {
    if (!grade || excelBusy) return;
    setExcelBusy(true);
    try {
      await ProgramCandidatesAdminApi.exportResult(grade, academicYear || undefined);
    } finally {
      setExcelBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
        <Field label="Grade">
          <Select value={grade} onChange={(e) => setGrade(e.target.value)} className="!w-40">
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Program Item">
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)} className="!w-64">
            <option value="">Select item…</option>
            {itemsForGrade.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.code ? `(${i.code})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <button className="btn-secondary flex items-center gap-1.5" disabled={!grade || callSheetBusy} onClick={callSheet} title={!grade ? 'Select a grade first' : undefined}>
          {callSheetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Call Sheet
        </button>
        <button className="btn-secondary flex items-center gap-1.5" disabled={!grade || excelBusy} onClick={exportExcel} title={!grade ? 'Select a grade first' : undefined}>
          {excelBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
          Export Excel
        </button>
      </div>

      {!itemId ? (
        <div className="card p-8 text-center text-slate-400">Select a program item to see its A-grade top scorers.</div>
      ) : isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : results.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No A-grade candidates for this item yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Chest #</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Mark</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Certificate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((r) => (
                <ItemResultRowView key={r.candidateItemId} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───── Scoreboard ────────────────────────────────────────────────────────────
function ScoreboardTab() {
  const { data: items = [] } = useQuery({ queryKey: ['program-items-catalog-sa'], queryFn: () => ProgramItemsApi.list() });
  const grades = useMemo(() => [...new Set(items.map((i) => i.gradeCategory).filter(Boolean))] as string[], [items]);
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();
  const [grade, setGrade] = useState('');

  const { data: scoreboard, isFetching } = useQuery({
    queryKey: ['program-scoreboard', grade, academicYear],
    queryFn: () => ProgramDashboardApi.scoreboard(grade || undefined, academicYear || undefined),
  });

  const itemIds = Object.keys(scoreboard?.items ?? {});

  return (
    <div>
      <div className="mb-4 flex items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
        <Field label="Grade">
          <Select value={grade} onChange={(e) => setGrade(e.target.value)} className="!w-40">
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !scoreboard || scoreboard.schools.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No judged results yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                {itemIds.map((id) => (
                  <th key={id} className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {scoreboard.items[id]}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scoreboard.schools.map((s, i) => (
                <tr key={s.schoolId} className={i === 0 ? 'bg-amber-50/50' : 'hover:bg-slate-50'}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-2.5 font-medium text-slate-900">
                    {i + 1}. {s.schoolName}
                  </td>
                  {itemIds.map((id) => (
                    <td key={id} className="px-3 py-2.5 text-center text-slate-600">
                      {s.items[id] ?? '—'}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{s.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───── Exports ───────────────────────────────────────────────────────────────
function ExportsTab() {
  const { data: schoolsPage } = useQuery({ queryKey: ['schools-for-program-export'], queryFn: () => SchoolsApi.list({ limit: 100 }) });
  const schools = schoolsPage?.items ?? [];
  const { data: items = [] } = useQuery({ queryKey: ['program-items-catalog-sa'], queryFn: () => ProgramItemsApi.list() });
  const grades = useMemo(() => [...new Set(items.map((i) => i.gradeCategory).filter(Boolean))] as string[], [items]);
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();

  const [type, setType] = useState<ExportType>('results');
  const [grade, setGrade] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await ProgramDashboardApi.exportData(type, format, {
        grade: grade || undefined,
        schoolId: type === 'participant-list' ? schoolId : undefined,
        academicYear: academicYear || undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card max-w-xl space-y-4 p-6">
      <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
      <Field label="Export type">
        <Select value={type} onChange={(e) => setType(e.target.value as ExportType)}>
          <option value="results">Results (all items)</option>
          <option value="call-sheet">Call Sheet (grouped by item)</option>
          <option value="participant-list">Participant List (one school)</option>
        </Select>
      </Field>
      <Field label="Grade (optional)">
        <Select value={grade} onChange={(e) => setGrade(e.target.value)}>
          <option value="">All grades</option>
          {grades.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </Select>
      </Field>
      {type === 'participant-list' && (
        <Field label="School" required>
          <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">Select school…</option>
            {schools.map((s: any) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <div className="flex gap-2 pt-2">
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => runExport('xlsx')} disabled={!!busy || (type === 'participant-list' && !schoolId)}>
          {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
          Excel
        </button>
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => runExport('csv')} disabled={!!busy || (type === 'participant-list' && !schoolId)}>
          {busy === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4 text-slate-600" />}
          CSV
        </button>
        <button className="btn-secondary flex items-center gap-1.5" onClick={() => runExport('pdf')} disabled={!!busy || (type === 'participant-list' && !schoolId)}>
          {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
          PDF
        </button>
      </div>
    </div>
  );
}

// ───── Candidates (Part 4) ───────────────────────────────────────────────────
function CandidatesTab() {
  const qc = useQueryClient();
  const { data: schoolsPage } = useQuery({ queryKey: ['schools-for-program-export'], queryFn: () => SchoolsApi.list({ limit: 100 }) });
  const schools = schoolsPage?.items ?? [];
  const { data: items = [] } = useQuery({ queryKey: ['program-items-catalog-sa'], queryFn: () => ProgramItemsApi.list() });
  const grades = useMemo(() => [...new Set(items.map((i) => i.gradeCategory).filter(Boolean))] as string[], [items]);

  const [schoolId, setSchoolId] = useState('');
  const [grade, setGrade] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const { data, isFetching } = useQuery({
    queryKey: ['program-candidates-admin', schoolId, grade, search, page, limit],
    queryFn: () => ProgramCandidatesAdminApi.list({ schoolId: schoolId || undefined, grade: grade || undefined, search: search || undefined, page, limit }),
  });

  const remove = useMutation({
    mutationFn: (c: AdminProgramCandidate) => ProgramCandidatesAdminApi.remove(c.id, c.schoolId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['program-candidates-admin'] }),
  });

  const [editing, setEditing] = useState<AdminProgramCandidate | null>(null);
  const [editChest, setEditChest] = useState('');
  const update = useMutation({
    mutationFn: () => ProgramCandidatesAdminApi.update(editing!.id, { schoolId: editing!.schoolId, chestNumber: editChest ? Number(editChest) : undefined }),
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['program-candidates-admin'] });
    },
  });

  const [exportGrade, setExportGrade] = useState('');
  const [exportSchoolId, setExportSchoolId] = useState('');
  const exportSchoolName = schools.find((s: any) => s.id === exportSchoolId)?.name ?? 'school';
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const runGradeExport = async (key: string, fn: (grade: string) => Promise<void>) => {
    if (!exportGrade || exportBusy) return;
    setExportBusy(key);
    try {
      await fn(exportGrade);
    } finally {
      setExportBusy(null);
    }
  };
  const runSchoolExport = async (key: string, fn: (schoolId: string, schoolName: string) => Promise<void>) => {
    if (!exportSchoolId || exportBusy) return;
    setExportBusy(key);
    try {
      await fn(exportSchoolId, exportSchoolName);
    } finally {
      setExportBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="School">
          <Select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setPage(1); }} className="!w-52">
            <option value="">All schools</option>
            {schools.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Grade (student's class)">
          <Select value={grade} onChange={(e) => { setGrade(e.target.value); setPage(1); }} className="!w-44">
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </Select>
        </Field>
        <Field label="Search">
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Name, admission no, chest #…" className="!w-56" />
        </Field>
      </div>

      <div className="mb-4 card space-y-3 p-4">
        <div className="text-sm font-semibold text-slate-700">Cross-school exports</div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Grade (for Candidates / Items / Results exports)">
            <Select value={exportGrade} onChange={(e) => setExportGrade(e.target.value)} className="!w-56">
              <option value="">Select grade…</option>
              {grades.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>
          </Field>
          <button className="btn-secondary flex items-center gap-1.5" disabled={!exportGrade || !!exportBusy} onClick={() => runGradeExport('candidates', ProgramCandidatesAdminApi.exportCandidates)}>
            {exportBusy === 'candidates' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
            Candidates
          </button>
          <button className="btn-secondary flex items-center gap-1.5" disabled={!exportGrade || !!exportBusy} onClick={() => runGradeExport('items', ProgramCandidatesAdminApi.exportCandidateItems)}>
            {exportBusy === 'items' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
            Candidate Items
          </button>
          <button className="btn-secondary flex items-center gap-1.5" disabled={!exportGrade || !!exportBusy} onClick={() => runGradeExport('result', ProgramCandidatesAdminApi.exportResult)}>
            {exportBusy === 'result' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
            Results
          </button>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="School (for Participants exports)">
            <Select value={exportSchoolId} onChange={(e) => setExportSchoolId(e.target.value)} className="!w-56">
              <option value="">Select school…</option>
              {schools.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Select>
          </Field>
          <button className="btn-secondary flex items-center gap-1.5" disabled={!exportSchoolId || !!exportBusy} onClick={() => runSchoolExport('participants-xlsx', ProgramCandidatesAdminApi.exportParticipantsExcel)}>
            {exportBusy === 'participants-xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
            Participants (Excel)
          </button>
          <button className="btn-secondary flex items-center gap-1.5" disabled={!exportSchoolId || !!exportBusy} onClick={() => runSchoolExport('participants-pdf', ProgramCandidatesAdminApi.exportParticipantsPdf)}>
            {exportBusy === 'participants-pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
            Participants (PDF)
          </button>
        </div>
      </div>

      {isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !data || data.items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No candidates found.</div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Chest #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-slate-800">
                      {c.studentName}
                      <div className="text-xs text-slate-400">{c.admissionNo}</div>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{c.schoolName}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{c.className} {c.sectionName}</td>
                    <td className="px-4 py-2.5 text-center text-sm text-slate-500">{c.chestNumber ?? '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">
                      {c.items.map((i) => (
                        <span key={i.id} className="mr-1 inline-block">
                          {i.programItemName}{i.isGroupLeader ? ' (L)' : ''}
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button className="mr-2 text-slate-500 hover:text-brand-600" title="Edit chest number" onClick={() => { setEditing(c); setEditChest(String(c.chestNumber ?? '')); }}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        className="text-slate-500 hover:text-red-600"
                        title="Delete registration"
                        onClick={() => {
                          if (window.confirm(`Delete ${c.studentName}'s entire program registration? This removes all ${c.items.length} item(s).`)) remove.mutate(c);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={data.totalPages} total={data.total} limit={limit} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="card w-full max-w-sm space-y-4 p-5">
            <div className="text-sm font-semibold text-slate-800">Edit chest number — {editing.studentName}</div>
            <Field label="Chest number">
              <Input type="number" min={0} value={editChest} onChange={(e) => setEditChest(e.target.value)} />
            </Field>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-primary" disabled={update.isPending} onClick={() => update.mutate()}>
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───── Registration Stats (Part 4) ───────────────────────────────────────────
function RegistrationStatsTab() {
  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();
  const { data, isFetching } = useQuery({
    queryKey: ['program-registration-stats', academicYear],
    queryFn: () => ProgramDashboardAdminApi.registrationStats(academicYear || undefined),
  });

  if (isFetching || !data) return <div className="card p-8 text-center text-slate-400">Loading…</div>;

  const totalMale = data.bySchool.reduce((s, r) => s + r.male, 0);
  const totalFemale = data.bySchool.reduce((s, r) => s + r.female, 0);
  const totalAll = data.bySchool.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={setAcademicYear} years={academicYears} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card flex items-center gap-3 p-5">
          <Users className="h-8 w-8 text-blue-500" />
          <div>
            <div className="text-xs font-medium uppercase text-slate-500">Male</div>
            <div className="text-2xl font-bold text-slate-900">{totalMale}</div>
          </div>
        </div>
        <div className="card flex items-center gap-3 p-5">
          <Users className="h-8 w-8 text-pink-500" />
          <div>
            <div className="text-xs font-medium uppercase text-slate-500">Female</div>
            <div className="text-2xl font-bold text-slate-900">{totalFemale}</div>
          </div>
        </div>
        <div className="card flex items-center gap-3 p-5">
          <Users className="h-8 w-8 text-slate-500" />
          <div>
            <div className="text-xs font-medium uppercase text-slate-500">Total registered students</div>
            <div className="text-2xl font-bold text-slate-900">{totalAll}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-slate-700">By School</div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Male</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Female</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.bySchool.filter((s) => s.total > 0).sort((a, b) => b.total - a.total).map((s) => (
                <tr key={s.schoolId} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-800">{s.schoolName}</td>
                  <td className="px-4 py-2 text-center text-slate-600">{s.male}</td>
                  <td className="px-4 py-2 text-center text-slate-600">{s.female}</td>
                  <td className="px-4 py-2 text-center font-semibold text-slate-900">{s.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-slate-700">By Grade</div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Male</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Female</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.byGrade.map((g) => (
                <tr key={g.grade} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-800">{g.grade}</td>
                  <td className="px-4 py-2 text-center text-slate-600">{g.male}</td>
                  <td className="px-4 py-2 text-center text-slate-600">{g.female}</td>
                  <td className="px-4 py-2 text-center font-semibold text-slate-900">{g.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───── All Students (Part 4) ─────────────────────────────────────────────────
function AllStudentsTab({ initialGrade = '' }: { initialGrade?: string }) {
  // GET /superadmin/schools is SuperadminGuard-only — an org-admin token
  // got a silent 401/403 here, leaving the School filter dropdown always
  // empty for org tier (confirmed the actual cause of the reported "School
  // filter not working"). Use the org-scoped schools list instead when the
  // caller is organization-tier.
  const isOrgTier = useAuthStore((s) => s.user?.scope === 'organization');
  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-program-export', isOrgTier],
    queryFn: async (): Promise<{ id: string; name: string }[]> =>
      isOrgTier ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
  });
  const { data: items = [] } = useQuery({ queryKey: ['program-items-catalog-sa'], queryFn: () => ProgramItemsApi.list() });
  // Competition grade bracket (program_items.grade_category, e.g. "Grade 3")
  // — NOT the student's own school class, a different concept the backend
  // also calls "grade" but filters on a different column entirely.
  const gradeCategories = useMemo(() => [...new Set(items.map((i) => i.gradeCategory).filter(Boolean))] as string[], [items]);

  const { academicYears, academicYear, setAcademicYear } = useProgramYearFilter();

  const [schoolId, setSchoolId] = useState('');
  const [gender, setGender] = useState('');
  const [gradeCategory, setGradeCategory] = useState(initialGrade);
  const [programItemId, setProgramItemId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [detailsFor, setDetailsFor] = useState<{ schoolId: string; candidateId: string } | null>(null);

  // "View All" on a Dashboard grade card lands here with a grade preset —
  // re-sync if the Dashboard sends a different grade on a later visit.
  useEffect(() => {
    if (initialGrade) {
      setGradeCategory(initialGrade);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGrade]);

  const { data, isFetching } = useQuery({
    queryKey: ['program-all-students', schoolId, gender, gradeCategory, programItemId, search, academicYear, page, limit],
    queryFn: () =>
      ProgramDashboardAdminApi.allStudents({
        schoolId: schoolId || undefined,
        gender: gender || undefined,
        gradeCategory: gradeCategory || undefined,
        programItemId: programItemId || undefined,
        search: search || undefined,
        academicYear: academicYear || undefined,
        page,
        limit,
      }),
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <YearFilterField value={academicYear} onChange={(v) => { setAcademicYear(v); setPage(1); }} years={academicYears} />
        <Field label="School">
          <Select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setPage(1); }} className="!w-52">
            <option value="">All schools</option>
            {schools.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Grade">
          <Select value={gradeCategory} onChange={(e) => { setGradeCategory(e.target.value); setPage(1); }} className="!w-36">
            <option value="">All grades</option>
            {gradeCategories.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </Select>
        </Field>
        <Field label="Gender">
          <Select value={gender} onChange={(e) => { setGender(e.target.value); setPage(1); }} className="!w-36">
            <option value="">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Select>
        </Field>
        <Field label="Program Item">
          <Select value={programItemId} onChange={(e) => { setProgramItemId(e.target.value); setPage(1); }} className="!w-56">
            <option value="">All items</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>{i.name} {i.gradeCategory ? `(${i.gradeCategory})` : ''}</option>
            ))}
          </Select>
        </Field>
        <Field label="Search">
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Name or mobile…" className="!w-56" />
        </Field>
      </div>

      {isFetching ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !data || data.items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No students found.</div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Gender</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Mobile</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Chest #</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Programs</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((s) => (
                  <tr key={s.candidateId} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-800">{s.studentName}</td>
                    <td className="px-4 py-2 text-slate-600">{s.schoolName}</td>
                    <td className="px-4 py-2 text-slate-600">{s.studentGrade ?? '—'}</td>
                    <td className="px-4 py-2 capitalize text-slate-600">{s.gender ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-600">{s.mobile ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-center text-slate-500">{s.chestNumber ?? '—'}</td>
                    <td className="px-4 py-2 text-center font-semibold text-slate-900">{s.totalPrograms}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                        onClick={() => setDetailsFor({ schoolId: s.schoolId, candidateId: s.candidateId })}
                      >
                        <Eye className="h-3.5 w-3.5" /> View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={data.totalPages} total={data.total} limit={limit} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        </>
      )}

      <CandidateDetailsModal target={detailsFor} onClose={() => setDetailsFor(null)} />
    </div>
  );
}

/** Real legacy's "Program Items Details" modal — student/school/chest number header, one row per registered item (Program Item / Grade category / Mark / Grade / Rank). */
function CandidateDetailsModal({ target, onClose }: { target: { schoolId: string; candidateId: string } | null; onClose: () => void }) {
  const { data, isFetching } = useQuery({
    queryKey: ['program-candidate-details', target?.schoolId, target?.candidateId],
    queryFn: () => ProgramDashboardAdminApi.candidateItemDetails(target!.schoolId, target!.candidateId),
    enabled: !!target,
  });

  return (
    <Modal open={!!target} onClose={onClose} title="Program Items Details" size="lg">
      {isFetching || !data ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          <div className="mb-4 space-y-1 text-sm">
            <div><span className="font-medium text-slate-700">Student Name:</span> {data.studentName}</div>
            <div><span className="font-medium text-slate-700">School:</span> {data.schoolName}</div>
            <div><span className="font-medium text-slate-700">Chest Number:</span> {data.chestNumber ?? '—'}</div>
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">S.No</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Program Item</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Mark</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Grade</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-400">No program items.</td>
                  </tr>
                ) : (
                  data.items.map((r) => (
                    <tr key={r.snNo}>
                      <td className="px-3 py-2 text-slate-600">{r.snNo}</td>
                      <td className="px-3 py-2 text-slate-800">{r.programItemName}</td>
                      <td className="px-3 py-2 text-slate-600">{r.gradeCategory ?? '—'}</td>
                      <td className="px-3 py-2 text-center text-slate-600">{r.judgeMark ?? '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {r.grade ? <Badge tone={GRADE_TONE[r.grade] ?? 'slate'}>{r.grade}</Badge> : '—'}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-600">{r.rank ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
